import {
  AssemblyModalities,
  AssemblyType,
  SituationValues,
  StepIds,
  StepStatus,
  Toggle,
} from 'helpers/enums/Assembleia';
import { createContext } from 'react';
import { RegisterContextActions } from './RegisterContextActions';

type Nucleus = {
  id: number;
  code: string;
};

type NucleusItem = {
  [key: string]: Nucleus[];
};

type NucleusType = {
  nucleusesQuantity: number;
  nucleuses: NucleusItem;
};

export const NUCLEU_INITIAL_STATE: NucleusType = {
  nucleusesQuantity: 0,
  nucleuses: {},
};

interface DataInputs {
  title: string;
  cooperative: string;
  type: AssemblyType | undefined;
  modality: AssemblyModalities | undefined;
  description: string | undefined;
  link: string;
  quantityAssociates: string | number | null;
  microsoftTeams: Toggle | undefined;
  flgEventTest: boolean;
  startDate: string;
  startHour: string;
  endDate: string;
  endHour: string;
  hybridStartDate: string;
  hybridStartHour: string;
  hybridEndDate: string;
  hybridEndHour: string;
  tmpQuantityAssociates: string | number | null;
  tmpStartDate: string;
  tmpStartHour: string;
  flagAssociate?: boolean;
  flagCoordinator?: boolean;
}

export interface IInitialStateProps {
  data: {
    campos: DataInputs;
    type: string[];
    modalities: string[];
    cooperativeOptions: { label: string; value: string }[];
    link: {
      [key in AssemblyType]: AssemblyModalities[];
    };
    tiposLoading: boolean;
    cooperativeLoading: boolean;
    modalitiesLoading: boolean;
    linkLoading: boolean;
  };
  dataOld: {
    campos: DataInputs;
  };
  entities: {
    inputs: {
      agency: string[];
      nucleou: number[];
    };
    agencyOptions: { label: string; value: string }[];
    nucleouOptions: NucleusType;
    tableData: {
      agn: { label: string; value: string };
      id: string;
      nuc: any;
    }[];
    allNucPerAgency: { agn: string; nuc: { id: number; code: string }[] }[];
    agencyLoading: boolean;
    nucleouLoading: boolean;
  };
  entitiesOld: {
    tableData: {
      agn: { label: string; value: string };
      id: string;
      nuc: any;
    }[];
  };
  local: {
    inputs: {
      postcode: string;
      state: string;
      city: string;
      neighborhood: string;
      address: string;
      number: string;
      localReference: string;
    };
    stateOptions: { label: string; value: string }[];
    citiesOptions: { label: string; value: string }[];
    loadData: boolean;
  };
  localOld: {
    inputs: {
      postcode: string;
      state: string;
      city: string;
      neighborhood: string;
      address: string;
      number: string;
      localReference: string;
    };
  };
  staves: {
    agendas: any[];
    agendasOld: any[];
  };
  survey: {
    enabled: boolean | undefined;
    enabledOld: boolean | undefined;
    template: any;
    templateOld: any;
    templates: {
      oidTemplatePesquisa: string;
      codigo: number;
      nome: string;
      flgTemplatePadrao: boolean;
      modeloPesquisa: string;
    }[];
    templatesOld: {
      oidTemplatePesquisa: string;
      codigo: number;
      nome: string;
      flgTemplatePadrao: boolean;
      modeloPesquisa: string;
    }[];
    templateLoad: boolean;
  };
  templateId: string | null;
  attachments: {
    files: any[];
  };
  completed: { step: StepIds; completed: boolean }[];
  stepId: StepIds;
  status: StepStatus;
  initialStatus: SituationValues | undefined;
  eventStatus: SituationValues | null;
  maximumQuantityAssociates: number;
  showAlert: boolean;
  toPage: string;
}

export const INITIAL_STATE = {
  data: {
    campos: {
      title: '',
      cooperative: '',
      type: undefined,
      modality: undefined,
      description: '',
      link: '',
      quantityAssociates: null,
      microsoftTeams: undefined,
      flgEventTest: false,
      startDate: '',
      startHour: '',
      endDate: '',
      endHour: '',
      hybridStartDate: '',
      hybridEndDate: '',
      hybridStartHour: '',
      hybridEndHour: '',
      tmpQuantityAssociates: null,
      tmpStartDate: '',
      tmpStartHour: '',
      flagAssociate: false,
      flagCoordinator: true,
    },
    type: [],
    modalities: [],
    cooperativeOptions: [],
    link: { AGE: [], AGN: [], AGO: [], AOE: [], REU: [] },
    tiposLoading: false,
    cooperativeLoading: false,
    modalitiesLoading: false,
    linkLoading: false,
  },
  dataOld: {
    campos: {
      title: '',
      cooperative: '',
      type: undefined,
      modality: undefined,
      description: '',
      link: '',
      quantityAssociates: null,
      microsoftTeams: undefined,
      flgEventTest: false,
      startDate: '',
      startHour: '',
      endDate: '',
      endHour: '',
      hybridStartDate: '',
      hybridEndDate: '',
      hybridStartHour: '',
      hybridEndHour: '',
      tmpQuantityAssociates: null,
      tmpStartDate: '',
      tmpStartHour: '',
      flagAssociate: false,
      flagCoordinator: true,
    },
  },
  entities: {
    inputs: {
      agency: [],
      nucleou: [],
    },
    agencyOptions: [],
    nucleouOptions: NUCLEU_INITIAL_STATE,
    tableData: [],
    allNucPerAgency: [],
    agencyLoading: false,
    nucleouLoading: false,
  },
  entitiesOld: {
    tableData: [],
  },
  local: {
    inputs: {
      postcode: '',
      state: '',
      city: '',
      neighborhood: '',
      address: '',
      number: '',
      localReference: '',
    },
    stateOptions: [],
    citiesOptions: [],
    loadData: false,
  },
  localOld: {
    inputs: {
      postcode: '',
      state: '',
      city: '',
      neighborhood: '',
      address: '',
      number: '',
      localReference: '',
    },
  },
  staves: {
    agendas: [],
    agendasOld: [],
  },
  survey: {
    enabled: undefined,
    enabledOld: undefined,
    templates: [],
    template: undefined,
    templateOld: undefined,
    templatesOld: [],
    templateLoad: false,
  },
  templateId: '',
  attachments: {
    files: [],
  },
  completed: [
    { step: StepIds.data, completed: false },
    { step: StepIds.entity, completed: false },
    { step: StepIds.local, completed: false },
    { step: StepIds.staves, completed: false },
    { step: StepIds.survey, completed: false },
    { step: StepIds.attachment, completed: false },
  ],
  stepId: StepIds.data,
  status: StepStatus.CRIACAO,
  initialStatus: undefined,
  eventStatus: null,
  maximumQuantityAssociates: 34000,
  showAlert: false,
  toPage: '',
};

export type InitialStateType = typeof INITIAL_STATE;

export const RegisterContext = createContext({
  state: INITIAL_STATE,
  setStepCompleted: (_: { step: string; completed: boolean }[]) => {},
  setStepId: (_: StepIds) => {},
  setStepStatus: (_: StepStatus) => {},
  dataStepSetFilter: (_: {
    title?: string;
    cooperative?: string;
    type?: AssemblyType;
    modality?: AssemblyModalities | string;
    description?: string;
    link?: string;
    quantityAssociates?: string | number | null;
    microsoftTeams?: Toggle | string;
    flgEventTest?: boolean;
    startDate?: string;
    startHour?: string;
    endDate?: string;
    endHour?: string;
    hybridStartDate?: string;
    hybridStartHour?: string;
    hybridEndDate?: string;
    hybridEndHour?: string;
    tmpQuantityAssociates?: string | number | null;
    tmpStartDate?: string;
    tmpStartHour?: string;
    flagAssociate?: boolean;
    flagCoordinator?: boolean;
  }) => {},
  dataStepSetFilterOld: (_: {
    title?: string;
    cooperative?: string;
    type?: AssemblyType;
    modality?: AssemblyModalities | string;
    description?: string;
    link?: string;
    quantityAssociates?: string | number | null;
    microsoftTeams?: Toggle | string;
    flgEventTest?: boolean;
    startDate?: string;
    startHour?: string;
    endDate?: string;
    endHour?: string;
    hybridStartDate?: string;
    hybridStartHour?: string;
    hybridEndDate?: string;
    hybridEndHour?: string;
    tmpQuantityAssociates?: string | number | null;
    tmpStartDate?: string;
    tmpStartHour?: string;
    flagAssociate?: boolean;
    flagCoordinator?: boolean;
  }) => {},
  entitiesStepSetFilter: (_: { agency?: string[]; nucleou?: number[] }) => {},
  localStepSetFilter: (_: {
    postcode?: string;
    state?: string;
    city?: string;
    neighborhood?: string;
    address?: string;
    number?: string;
    localReference?: string;
  }) => {},
  localStepSetFilterOld: (_: {
    postcode?: string;
    state?: string;
    city?: string;
    neighborhood?: string;
    address?: string;
    number?: string;
    localReference?: string;
  }) => {},
  surveyStepSetFilter: (_?: any) => {},
  surveyStepSetFilterOld: (_?: any) => {},
  changeState: (_type: RegisterContextActions, _payload: any) => {},
});
